import React, { useState } from "react";
import { observer } from "mobx-react";

// Coach proposed a query rewrite — render in place of the user's bubble
// while the proposal is pending. On accept/edit/reject the LLMClient
// truncates and replays from this turn with the chosen text.

const TYPE_LABELS = {
  vocab:   "Vocabulary",
  scope:   "Scope",
  anchor:  "Anchor",
  resolve: "Disambiguation",
};

const TYPE_COLORS = {
  vocab:   { fg: "#9a3412", bg: "#fed7aa" },   // amber
  scope:   { fg: "#1e40af", bg: "#bfdbfe" },   // blue
  anchor:  { fg: "#166534", bg: "#bbf7d0" },   // green
  resolve: { fg: "#6b21a8", bg: "#e9d5ff" },   // purple
};

const QueryRewriteCard = observer(({ turn, turnIndex, client }) => {
  const proposal = turn?.rewrite;
  if (!proposal) return null;

  const original = turn.content?.[0]?.text ?? "";
  const [draft, setDraft] = useState(proposal.rewritten || "");
  const [showWhy, setShowWhy] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = (text) => {
    if (submitting) return;
    setSubmitting(true);
    client.acceptRewrite(turnIndex, text);
  };

  const handleRunIt = () => submit(draft);
  const handleUseOriginal = () => submit(original);

  return (
    <div style={{
      maxWidth: "min(720px, 92%)",
      margin: "8px 16px 12px auto",
      background: "#fffbeb",
      border: "1px solid #fcd34d",
      borderRadius: "8px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
      fontFamily: "inherit",
      overflow: "hidden",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 14px",
        background: "#fef3c7",
        borderBottom: "1px solid #fcd34d",
        fontSize: "12px",
        color: "#92400e",
        fontWeight: 600,
      }}>
        <span>{"✨"}</span>
        <span>Coach is reviewing your query</span>
      </div>

      <div style={{ padding: "12px 14px" }}>
        <div style={{
          color: "#9a3412",
          fontSize: "11px",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          marginBottom: "2px",
        }}>You typed</div>
        <div style={{
          color: "#78350f",
          fontSize: "13px",
          fontStyle: "italic",
          marginBottom: "12px",
          paddingLeft: "8px",
          borderLeft: "2px solid #fcd34d",
        }}>{original}</div>

        <div style={{
          color: "#9a3412",
          fontSize: "11px",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          marginBottom: "4px",
        }}>Coach proposes (editable)</div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={Math.max(2, Math.min(6, draft.split("\n").length + 1))}
          disabled={submitting}
          style={{
            width: "100%",
            padding: "8px 10px",
            fontSize: "14px",
            lineHeight: "1.45",
            border: "1px solid #fcd34d",
            borderRadius: "5px",
            background: "#fff",
            fontFamily: "inherit",
            resize: "vertical",
            color: "#1f2937",
            boxSizing: "border-box",
          }}
        />

        {Array.isArray(proposal.changes) && proposal.changes.length > 0 && (
          <div style={{ marginTop: "10px" }}>
            <button
              onClick={() => setShowWhy(s => !s)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: "#92400e",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {showWhy ? "Hide" : "Show"} why these changes
            </button>
            {showWhy && (
              <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {proposal.changes.map((c, i) => {
                  const colors = TYPE_COLORS[c.type] || TYPE_COLORS.vocab;
                  return (
                    <div key={i} style={{
                      padding: "8px 10px",
                      background: "#fff",
                      border: "1px solid #fde68a",
                      borderLeft: `3px solid ${colors.fg}`,
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#374151",
                    }}>
                      <div style={{ display: "flex", gap: "6px", alignItems: "baseline", marginBottom: "3px", flexWrap: "wrap" }}>
                        <span style={{
                          color: colors.fg,
                          background: colors.bg,
                          padding: "1px 6px",
                          borderRadius: "3px",
                          fontSize: "10px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}>{TYPE_LABELS[c.type] || c.type}</span>
                        <span style={{ color: "#6b7280" }}>
                          {c.before ? <s>{c.before}</s> : <em>(none)</em>}
                          {" → "}
                          <strong style={{ color: "#1f2937" }}>{c.after}</strong>
                        </span>
                      </div>
                      <div style={{ color: "#4b5563", lineHeight: "1.5" }}>{c.reason}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "12px" }}>
          <button
            onClick={handleUseOriginal}
            disabled={submitting}
            style={{
              padding: "6px 14px",
              fontSize: "12px",
              cursor: submitting ? "not-allowed" : "pointer",
              color: "#92400e",
              background: "transparent",
              border: "1px solid #d97706",
              borderRadius: "4px",
              fontFamily: "inherit",
            }}
          >Use original</button>
          <button
            onClick={handleRunIt}
            disabled={submitting || !draft.trim()}
            style={{
              padding: "6px 14px",
              fontSize: "12px",
              cursor: submitting ? "wait" : "pointer",
              color: "#fff",
              background: "#d97706",
              border: "1px solid #d97706",
              borderRadius: "4px",
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >{submitting ? "Running…" : "Run it"}</button>
        </div>
      </div>
    </div>
  );
});

export default QueryRewriteCard;
